import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';

import { RECIPIENT_TYPE_SELF, TYPE } from 'apps/auth/constants/Constants';
import type { RootState } from 'apps/auth/redux/store';

type FormEntry = {
    fields?: Record<string, unknown>;
    [key: string]: unknown;
};

type FormState = Record<string, FormEntry | undefined>;
type MessagesState = {
    pageMessages?: unknown;
    complexMessages?: unknown;
    jsMessages?: unknown;
};
type HistoryState = {
    paymentHistory?: unknown;
    cheque?: {
        cheque?: unknown;
        extra?: unknown;
    };
};
type ExtraState = {
    typeOfTransfer?: string;
};

type PageMessagesState = MessagesState['pageMessages'];
type ComplexMessagesState = MessagesState['complexMessages'];
type JsMessagesState = MessagesState['jsMessages'];
type HistoryRecordsState = HistoryState['paymentHistory'];
type CheckMetaState = HistoryState['cheque'];
type CheckState = HistoryState['cheque'] extends { cheque?: infer T } ? T : unknown;
type ChequeExtraState = HistoryState['cheque'] extends { extra?: infer T } ? T : unknown;

export function useForm<TForm = FormEntry>(formName: string): TForm | undefined {
    return useSelector((state: RootState) => {
        const formState = (state as RootState & { form?: FormState }).form;
        return formState?.[formName] as TForm | undefined;
    });
}

export function useForms(): FormState | undefined {
    return useSelector((state: RootState) => (state as RootState & { form?: FormState }).form);
}

export function useLanguage<TLanguage = RootState['language']>(): TLanguage | undefined {
    return useSelector((state: RootState) => state.language as TLanguage | undefined);
}

export function useFields(formName: string): Record<string, unknown> | undefined {
    return useSelector((state: RootState) => {
        const formState = (state as RootState & { form?: FormState }).form;
        return formState?.[formName]?.fields;
    });
}

export function usePageMessages<TMessages = PageMessagesState>(): TMessages | undefined {
    return useSelector((state: RootState) => {
        const messages = (state as RootState & { messages?: MessagesState }).messages;
        return messages?.pageMessages as TMessages | undefined;
    });
}

export function useComplexMessage<TMessages = ComplexMessagesState>(): TMessages | undefined {
    return useSelector((state: RootState) => {
        const messages = (state as RootState & { messages?: MessagesState }).messages;
        return messages?.complexMessages as TMessages | undefined;
    });
}

export function useJsMessages<TMessages = JsMessagesState>(): TMessages | undefined {
    return useSelector((state: RootState) => {
        const messages = (state as RootState & { messages?: MessagesState }).messages;
        return messages?.jsMessages as TMessages | undefined;
    });
}

export function useApp<TApp = RootState['app']>(): TApp | undefined {
    return useSelector((state: RootState) => state.app as TApp | undefined);
}

export function useAuth<TAuth = RootState['auth']>(): TAuth | undefined {
    return useSelector((state: RootState) => state.auth as TAuth | undefined);
}

export function useExtra<TExtra = RootState['extra']>(): TExtra | undefined {
    return useSelector((state: RootState) => state.extra as TExtra | undefined);
}

export function useTypeOfTransfer(): string | null {
    const extra = useSelector((state: RootState) => (state as RootState & { extra?: ExtraState }).extra);
    const [searchParams] = useSearchParams();

    const searchType = searchParams.get(TYPE);
    if (extra?.typeOfTransfer && extra.typeOfTransfer !== searchType && searchType !== RECIPIENT_TYPE_SELF) {
        return extra.typeOfTransfer;
    }
    return searchType;
}

export function useRequest<TRequest = RootState['request']>(): TRequest | undefined {
    return useSelector((state: RootState) => state.request as TRequest | undefined);
}

export function useLogin<TLogin = RootState['login']>(): TLogin | undefined {
    return useSelector((state: RootState) => state.login as TLogin | undefined);
}

export function useHistoryRecords<THistory = HistoryRecordsState>(): THistory | undefined {
    return useSelector((state: RootState) => {
        const history = (state as RootState & { history?: HistoryState }).history;
        return history?.paymentHistory as THistory | undefined;
    });
}

export function useCheckMeta<TMeta = CheckMetaState>(): TMeta | undefined {
    return useSelector((state: RootState) => {
        const history = (state as RootState & { history?: HistoryState }).history;
        return history?.cheque as TMeta | undefined;
    });
}

export function useCheck<TCheck = CheckState>(): TCheck | undefined {
    return useSelector((state: RootState) => {
        const history = (state as RootState & { history?: HistoryState }).history;
        return history?.cheque?.cheque as TCheck | undefined;
    });
}

export function useChequeExtra<TExtra = ChequeExtraState>(): TExtra | undefined {
    return useSelector((state: RootState) => {
        const history = (state as RootState & { history?: HistoryState }).history;
        return history?.cheque?.extra as TExtra | undefined;
    });
}

export function useErrors<TErrors = unknown>(): TErrors | undefined {
    return useSelector((state: RootState) => (state as RootState & { errors?: TErrors }).errors);
}

export function useSend<TSend = RootState['send']>(): TSend | undefined {
    return useSelector((state: RootState) => state.send as TSend | undefined);
}
